/* ══════════════════════════════════════════════════════════════
   Financial Tracker — Global Market Indices & Movers
   S&P 500, NASDAQ, DJIA, FTSE, DAX, Nikkei
   ══════════════════════════════════════════════════════════════ */

import { fetchTopMovers } from '../services/market-data';
import { renderUnavailableBlock } from './data-state';

interface MarketIndex {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
}



let financialRefreshTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Fetch market data from Yahoo Finance v8 API (public, no key)
 */
async function fetchMarketIndices(): Promise<MarketIndex[]> {
  try {
    const symbols = ['^GSPC', '^IXIC', '^DJI', '^FTSE', '^GDAXI', '^N225'];
    const url = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${symbols.join(',')}`;

    const response = await fetch(url);
    if (!response.ok) throw new Error('Yahoo Finance API error');

    const data = await response.json() as {
      quoteResponse?: {
        result?: Array<{
          symbol: string;
          shortName?: string;
          regularMarketPrice?: number;
          regularMarketChange?: number;
          regularMarketChangePercent?: number;
        }>;
      };
    };

    if (data.quoteResponse?.result) {
      return data.quoteResponse.result.map(q => ({
        symbol: q.symbol,
        name: q.shortName || q.symbol,
        price: q.regularMarketPrice || 0,
        change: q.regularMarketChange || 0,
        changePercent: q.regularMarketChangePercent || 0,
      }));
    }

    return [];
  } catch {
    console.warn('[FinancialTracker] API failed');
    return [];
  }
}

function formatPrice(price: number): string {
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatChange(change: number, percent: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)} (${sign}${percent.toFixed(2)}%)`;
}

/**
 * Render Financial Tracker into a container
 */
export function renderFinancialTracker(container: HTMLElement): void {
  container.innerHTML = `
    <div class="wm-loading">
      <div class="wm-loading__spinner"></div>
      Loading market data...
    </div>
  `;

  loadFinancialData(container);

  // Refresh every 60 seconds
  financialRefreshTimer = setInterval(() => loadFinancialData(container), 60000);
}

async function loadFinancialData(container: HTMLElement): Promise<void> {
  const indices = await fetchMarketIndices();
  const movers = await fetchTopMovers();

  const gainers = movers?.gainers?.slice(0, 5).map(m => ({ symbol: m.symbol, name: m.symbol, changePercent: m.pChange })) || [];
  const losers = movers?.losers?.slice(0, 5).map(m => ({ symbol: m.symbol, name: m.symbol, changePercent: m.pChange })) || [];

  const indicesHtml = indices.map(idx => {
    const dir = idx.change >= 0 ? 'up' : 'down';
    const arrow = idx.change >= 0 ? '▲' : '▼';
    return `
      <div class="wm-finance-index">
        <div class="wm-finance-index__name">${idx.name}</div>
        <div class="wm-finance-index__price">${formatPrice(idx.price)}</div>
        <div class="wm-finance-index__change wm-finance-index__change--${dir}">
          ${arrow} ${formatChange(idx.change, idx.changePercent)}
        </div>
      </div>
    `;
  }).join('');

  const gainersHtml = gainers.map(s => `
    <div class="wm-finance-row">
      <span class="wm-finance-row__name">${s.symbol}</span>
      <span class="wm-finance-row__value--up">▲ +${s.changePercent.toFixed(2)}%</span>
    </div>
  `).join('');

  const losersHtml = losers.map(s => `
    <div class="wm-finance-row">
      <span class="wm-finance-row__name">${s.symbol}</span>
      <span class="wm-finance-row__value--down">▼ ${s.changePercent.toFixed(2)}%</span>
    </div>
  `).join('');

  if (indices.length === 0 && gainers.length === 0 && losers.length === 0) {
    container.innerHTML = renderUnavailableBlock('wm-empty', 'Live market data unavailable');
    return;
  }

  container.innerHTML = `
    <div class="wm-finance-indices">${indicesHtml || '<div class="wm-empty">Global index feed unavailable</div>'}</div>
    <div class="wm-finance-section">
      <div class="wm-finance-section__title">🟢 Top Gainers</div>
      ${gainersHtml || '<div class="wm-empty">Top gainers unavailable</div>'}
    </div>
    <div class="wm-finance-section">
      <div class="wm-finance-section__title">🔴 Top Losers</div>
      ${losersHtml || '<div class="wm-empty">Top losers unavailable</div>'}
    </div>
  `;
}

export function destroyFinancialTracker(): void {
  if (financialRefreshTimer) clearInterval(financialRefreshTimer);
}
